import { WebRtcPeer } from 'kurento-utils';
import { makeAutoObservable } from 'mobx';

class MediaServerStore {
  connection: WebSocket | null = null;
  webRtcPeer: WebRtcPeer | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  start(videoElement: any, rtspUrl: string) {
    if (!videoElement) {
      return;
    }

    this.createConnection();

    const setup = () => {
      const options = {
        localVideo: null,
        remoteVideo: videoElement,
        onicecandidate: this.onIceCandidate,
      };

      this.webRtcPeer = WebRtcPeer.WebRtcPeerRecvonly(options, (error) => {
        if (error) {
          console.log(error);
          return;
        }

        this.webRtcPeer?.generateOffer((error, offerSdp) => {
          if (error) {
            console.log(error);
            return;
          }

          const message = {
            id: 'start',
            sdpOffer: offerSdp,
            rtspUrl: rtspUrl,
          };
          this.sendMessage(message);
        });
      });
    };

    if (this.connection?.readyState === WebSocket.OPEN) {
      setup();
    } else {
      this.connection!.onopen = () => setup();
    }
  }

  stop() {
    if (this.webRtcPeer) {
      this.webRtcPeer.dispose();
      this.webRtcPeer = null;

      const message = { id: 'stop' };
      this.sendMessage(message);
    }

    this.cleanup();
  }

  startResponse(message: any) {
    this.webRtcPeer?.processAnswer(message.sdpAnswer);
  }

  cleanup() {
    this.connection?.close();
    this.connection = null;
  }

  private createConnection() {
    const signallerUrl = process.env.REACT_APP_MEDIA_SERVER_SIGNALER_URL;

    if (!signallerUrl) {
      return;
    }

    const socket = new WebSocket(signallerUrl);
    this.connection = socket;

    socket.onmessage = (message) => {
      const parsedMessage = JSON.parse(message.data);

      switch (parsedMessage.id) {
        case 'startResponse':
          this.startResponse(parsedMessage);
          break;
        case 'error':
          console.log('Error message from server: ' + parsedMessage.message);
          break;
        case 'iceCandidate':
          this.webRtcPeer?.addIceCandidate(parsedMessage.candidate);
          break;
        default:
          console.log('Unrecognized message');
      }
    };

    socket.onerror = (err) => {
      console.error('[WebSocket] error', err);
      socket.close();
    };
  }

  private sendMessage(message: any) {
    if (!this.connection || this.connection.readyState !== WebSocket.OPEN) {
      console.warn('[WebSocket] not ready, cannot send:', message);
      return;
    }

    const jsonMessage = JSON.stringify(message);
    this.connection.send(jsonMessage);
  }

  private onIceCandidate = (candidate: any) => {
    const message = {
      id: 'onIceCandidate',
      candidate: candidate,
    };
    this.sendMessage(message);
  };
}

const mediaServerContext = new MediaServerStore();
export default mediaServerContext;
